import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Shell from '../dashboard/Shell';
import FeatureGate from '../../components/FeatureGate';
import Modal from '../../components/ui/Modal';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import axiosInstance from '../../axiosConfig';
import { useToast } from '../../components/ui/Toast';
import { Line, Doughnut } from 'react-chartjs-2';
import {
  Link2, Plus, Copy, QrCode, Edit2, Trash2,
  Download, ChevronLeft, ChevronRight, Zap, Loader2,
} from 'lucide-react';
import './DeepLinks.css';

const PERIODS = [
  { value: '24h', label: 'Today' },
  { value: '7d',  label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
];

const PLATFORM_DOMAIN = process.env.REACT_APP_PLATFORM_DOMAIN || 'userevstar.com';

const CHART_COLORS = [
  '#0d7463', '#2563eb', '#7c3aed', '#ea580c', '#ca8a04',
  '#dc2626', '#059669', '#6366f1', '#ec4899', '#78716c',
];

const DEVICE_COLORS = ['#0d7463', '#2563eb', '#ea580c'];

const INITIAL_FORM = {
  destinationUrl: '', title: '', customAlias: '', campaign: '',
  utmSource: '', utmMedium: '', utmCampaign: '', utmTerm: '', utmContent: '',
  expiresAt: '', productId: '',
};

const formatNumber = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K';
  return n?.toString() || '0';
};

const formatCurrency = (n) =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency', currency: 'NGN', minimumFractionDigits: 0,
  }).format(n || 0);

const formatRevenue = (value, currency = '₦') => {
  if (!value || value === 0) return `${currency}0`;
  if (value >= 1_000_000) return `${currency}${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000)     return `${currency}${(value / 1_000).toFixed(1)}K`;
  return `${currency}${value.toLocaleString()}`;
};

export default function DeepLinks() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const [loading, setLoading] = useState(true);
  const [links, setLinks] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, pages: 0 });
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [period, setPeriod] = useState('30d');

  const [overview, setOverview] = useState(null);
  const [clickTrend, setClickTrend] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [devices, setDevices] = useState([]);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);

  /* ── Fetchers ────────────────────────────────────────── */
  const fetchLinks = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder,
      });
      if (search) params.set('search', search);
      const res = await axiosInstance.get(`/deep-links?${params}`);
      if (res.data.success) {
        setLinks(res.data.links);
        setPagination(res.data.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch links:', err);
    }
  }, [pagination.page, pagination.limit, search, sortBy, sortOrder]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [overviewRes, referrersRes, devicesRes] = await Promise.all([
        axiosInstance.get(`/deep-links/analytics/overview?period=${period}`),
        axiosInstance.get(`/deep-links/analytics/referrers?period=${period}`),
        axiosInstance.get(`/deep-links/analytics/devices?period=${period}`),
      ]);
      if (overviewRes.data.success) {
        setOverview(overviewRes.data.overview);
        setClickTrend(overviewRes.data.clickTrend);
      }
      if (referrersRes.data.success) setReferrers(referrersRes.data.referrers);
      if (devicesRes.data.success)   setDevices(devicesRes.data.devices);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    }
  }, [period]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchLinks(), fetchAnalytics()]);
      setLoading(false);
    };
    load();
  }, [fetchLinks, fetchAnalytics]);

  /* ── Handlers ────────────────────────────────────────── */
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Link copied', 'success');
    } catch {
      showToast('Failed to copy', 'error');
    }
  };

  const handleSave = async () => {
    if (!formData.destinationUrl) {
      showToast('Destination URL is required', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingLink) {
        const res = await axiosInstance.put(`/deep-links/${editingLink.id}`, formData);
        if (res.data.success) showToast('Link updated', 'success');
      } else {
        const res = await axiosInstance.post('/deep-links', formData);
        if (res.data.success) showToast('Deep link created', 'success');
      }
      closeModal();
      fetchLinks();
      fetchAnalytics();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save link', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (link) => {
    const ok = await confirm(
      `Delete "${link.title || link.shortCode}"? All click data will be lost.`,
      { title: 'Delete deep link?', confirmText: 'Delete', cancelText: 'Cancel', type: 'danger' }
    );
    if (!ok) return;
    try {
      await axiosInstance.delete(`/deep-links/${link.id}`);
      showToast('Link deleted', 'success');
      fetchLinks();
      fetchAnalytics();
    } catch {
      showToast('Failed to delete link', 'error');
    }
  };

  const handleQR = async (link) => {
    try {
      const res = await axiosInstance.get(`/deep-links/${link.id}/qr`);
      if (res.data.success) {
        const a = document.createElement('a');
        a.href = res.data.qrCode;
        a.download = `qr-${link.shortCode}.png`;
        a.click();
        showToast('QR code downloaded', 'success');
      }
    } catch {
      showToast('Failed to generate QR code', 'error');
    }
  };

  const handleExport = async () => {
    try {
      const res = await axiosInstance.get('/deep-links/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `deep-links-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast('Export downloaded', 'success');
    } catch {
      showToast('Failed to export', 'error');
    }
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setFormData({
      destinationUrl: link.destinationUrl,
      title:        link.title || '',
      customAlias:  link.customAlias || '',
      campaign:     link.campaign || '',
      utmSource:    link.utmSource || '',
      utmMedium:    link.utmMedium || '',
      utmCampaign:  link.utmCampaign || '',
      utmTerm:      link.utmTerm || '',
      utmContent:   link.utmContent || '',
      expiresAt:    link.expiresAt ? new Date(link.expiresAt).toISOString().split('T')[0] : '',
      productId:    link.productId || '',
    });
    setShowCreateModal(true);
  };

  const openCreate = () => {
    setEditingLink(null);
    setFormData(INITIAL_FORM);
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingLink(null);
    setFormData(INITIAL_FORM);
  };

  /* ── Chart data ──────────────────────────────────────── */
  const trendChartData = {
    labels: clickTrend.map(d =>
      new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    ),
    datasets: [
      {
        label: 'Total clicks',
        data: clickTrend.map(d => d.clicks),
        borderColor: '#0d7463',
        backgroundColor: 'rgba(13, 116, 99, 0.08)',
        fill: true, tension: 0.4, pointRadius: 2, pointHoverRadius: 5,
      },
      {
        label: 'Unique',
        data: clickTrend.map(d => d.uniqueClicks),
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.05)',
        fill: true, tension: 0.4, pointRadius: 2, pointHoverRadius: 5,
        borderDash: [4, 4],
      },
      {
        label: 'Revenue',
        data: clickTrend.map(d => d.revenue || 0),
        borderColor: '#ca8a04',
        backgroundColor: 'rgba(202, 138, 4, 0.06)',
        fill: true, tension: 0.4, pointRadius: 2, pointHoverRadius: 5,
        yAxisID: 'y1',
      },
    ],
  };

  const referrerChartData = {
    labels: referrers.slice(0, 8).map(r => r.source),
    datasets: [{
      data: referrers.slice(0, 8).map(r => r.clicks),
      backgroundColor: CHART_COLORS.slice(0, 8),
      borderWidth: 0, hoverOffset: 4,
    }],
  };

  const deviceChartData = {
    labels: devices.map(d => d.type?.charAt(0).toUpperCase() + d.type?.slice(1)),
    datasets: [{
      data: devices.map(d => d.clicks),
      backgroundColor: DEVICE_COLORS,
      borderWidth: 0, hoverOffset: 4,
    }],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { padding: 12, usePointStyle: true, font: { size: 11 } } },
    },
    cutout: '65%',
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', align: 'end', labels: { usePointStyle: true, font: { size: 11 } } },
    },
    scales: {
      x:  { grid: { display: false }, ticks: { font: { size: 10 } } },
      y:  { beginAtZero: true, grid: { color: '#f3f4f6' }, ticks: { font: { size: 10 } }, position: 'left' },
      y1: { beginAtZero: true, grid: { display: false }, ticks: { font: { size: 10 }, callback: (v) => formatRevenue(v) }, position: 'right' },
    },
  };

  /* ── Render ──────────────────────────────────────────── */
  if (loading) {
    return (
      <Shell>
        <FeatureGate
          featureId="marketing.deep_links"
          showUpgradePrompt
          upgradeMessage="Deep Links are available on Pro, Premium, and Enterprise plans."
        >
          <div className="dl-page">
            <div className="dl-loading">
              <Loader2 size={20} className="dl-spin" /> Loading…
            </div>
          </div>
        </FeatureGate>
      </Shell>
    );
  }

  return (
    <Shell>
      <FeatureGate
        featureId="marketing.deep_links"
        showUpgradePrompt
        upgradeMessage="Deep Links are available on Pro, Premium, and Enterprise plans. Track clicks, conversions, and revenue from every link you share."
      >
        <div className="dl-page">

          {/* ── Header ────────────────────────────────────── */}
          <header className="dl-header">
            <div className="dl-header-text">
              <h1 className="dl-title">Deep links</h1>
              <p className="dl-subtitle">Track, analyse, and optimise your marketing links.</p>
            </div>
            <div className="dl-header-actions">
              <button type="button" className="dl-btn dl-btn-secondary" onClick={handleExport}>
                <Download size={13} /> <span className="dl-btn-text">Export</span>
              </button>
              <button type="button" className="dl-btn dl-btn-primary" onClick={openCreate}>
                <Plus size={13} /> <span className="dl-btn-text">Create link</span>
              </button>
            </div>
          </header>

          {/* ── Date filter (scrollable on mobile) ────────── */}
          <div className="dl-tabs-wrap">
            <div className="dl-tabs">
              {PERIODS.map(p => (
                <button
                  key={p.value}
                  type="button"
                  className={`dl-tab${period === p.value ? ' is-active' : ''}`}
                  onClick={() => setPeriod(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Stat tiles ────────────────────────────────── */}
          {overview && (
            <section className="dl-stat-grid">
              <div className="dl-stat">
                <span className="dl-stat-label">Total links</span>
                <span className="dl-stat-value">{formatNumber(overview.totalLinks)}</span>
              </div>
              <div className="dl-stat">
                <span className="dl-stat-label">Total clicks</span>
                <span className="dl-stat-value">{formatNumber(overview.totalClicks)}</span>
              </div>
              <div className="dl-stat">
                <span className="dl-stat-label">Unique visitors</span>
                <span className="dl-stat-value">{formatNumber(overview.uniqueClicks)}</span>
              </div>
              <div className="dl-stat">
                <span className="dl-stat-label">Conversions</span>
                <span className="dl-stat-value dl-stat-value-success">{formatNumber(overview.totalConversions)}</span>
              </div>
              <div className="dl-stat">
                <span className="dl-stat-label">Revenue</span>
                <span className="dl-stat-value">{formatCurrency(overview.totalRevenue)}</span>
              </div>
            </section>
          )}

          {/* ── Charts ────────────────────────────────────── */}
          {clickTrend.length > 0 && (
            <section className="dl-card">
              <div className="dl-card-head">
                <h2 className="dl-card-title">Click trend</h2>
              </div>
              <div className="dl-chart-canvas">
                <Line data={trendChartData} options={lineOptions} />
              </div>
            </section>
          )}

          {(referrers.length > 0 || devices.length > 0) && (
            <div className="dl-chart-row">
              <section className="dl-card">
                <div className="dl-card-head">
                  <h2 className="dl-card-title">Traffic sources</h2>
                </div>
                <div className="dl-chart-canvas dl-chart-canvas-sm">
                  {referrers.length > 0 ? (
                    <Doughnut data={referrerChartData} options={doughnutOptions} />
                  ) : (
                    <p className="dl-chart-empty">No referrer data yet</p>
                  )}
                </div>
                {referrers.length > 0 && (
                  <div className="dl-legend">
                    <div className="dl-legend-head">
                      <span>Source</span>
                      <span>Clicks</span>
                      <span>Revenue</span>
                    </div>
                    {referrers.slice(0, 8).map((r, i) => (
                      <div key={i} className="dl-legend-row">
                        <span className="dl-legend-source">
                          <span className="dl-legend-dot" style={{ background: CHART_COLORS[i] }} />
                          {r.source}
                        </span>
                        <span>{formatNumber(r.clicks)}</span>
                        <span>{formatRevenue(r.revenue || 0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section className="dl-card">
                <div className="dl-card-head">
                  <h2 className="dl-card-title">Devices</h2>
                </div>
                <div className="dl-chart-canvas dl-chart-canvas-sm">
                  {devices.length > 0 ? (
                    <Doughnut data={deviceChartData} options={doughnutOptions} />
                  ) : (
                    <p className="dl-chart-empty">No device data yet</p>
                  )}
                </div>
                {devices.length > 0 && (
                  <div className="dl-legend">
                    <div className="dl-legend-head">
                      <span>Device</span>
                      <span>Clicks</span>
                      <span>Revenue</span>
                    </div>
                    {devices.map((d, i) => (
                      <div key={i} className="dl-legend-row">
                        <span className="dl-legend-source">
                          <span className="dl-legend-dot" style={{ background: DEVICE_COLORS[i] || '#9ca3af' }} />
                          {d.type?.charAt(0).toUpperCase() + d.type?.slice(1)}
                        </span>
                        <span>{formatNumber(d.clicks)}</span>
                        <span>{formatRevenue(d.revenue || 0)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}

          {/* ── Links list ────────────────────────────────── */}
          <section className="dl-card">
            <div className="dl-card-head">
              <h2 className="dl-card-title">
                All links <span className="dl-card-count">({pagination.total})</span>
              </h2>
            </div>

            {/* Filter row: search + sort (own row on mobile, side-by-side on desktop) */}
            {links.length > 0 && (
              <div className="dl-filter-row">
                <div className="dl-search">
                  <input
                    type="text"
                    className="dl-input"
                    placeholder="Search links…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>

                {/* Mobile sort select (hidden ≥720px) */}
                <select
                  className="dl-input dl-sort-select"
                  value={`${sortBy}:${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split(':');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  aria-label="Sort by"
                >
                  <option value="createdAt:desc">Newest first</option>
                  <option value="createdAt:asc">Oldest first</option>
                  <option value="totalClicks:desc">Most clicks</option>
                  <option value="totalConversions:desc">Most conversions</option>
                  <option value="totalRevenue:desc">Highest revenue</option>
                  <option value="title:asc">Title A–Z</option>
                </select>

                {/* Desktop sort chips (hidden &lt;720px) */}
                <div className="dl-sort-row" role="group" aria-label="Sort by">
                  <button
                    type="button"
                    className={`dl-sort-btn${sortBy === 'title' ? ' is-active' : ''}`}
                    onClick={() => handleSort('title')}
                  >
                    Link {sortBy === 'title' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                  </button>
                  <button
                    type="button"
                    className={`dl-sort-btn${sortBy === 'totalClicks' ? ' is-active' : ''}`}
                    onClick={() => handleSort('totalClicks')}
                  >
                    Clicks {sortBy === 'totalClicks' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                  </button>
                  <button
                    type="button"
                    className={`dl-sort-btn${sortBy === 'totalConversions' ? ' is-active' : ''}`}
                    onClick={() => handleSort('totalConversions')}
                  >
                    Conv. {sortBy === 'totalConversions' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                  </button>
                  <button
                    type="button"
                    className={`dl-sort-btn${sortBy === 'totalRevenue' ? ' is-active' : ''}`}
                    onClick={() => handleSort('totalRevenue')}
                  >
                    Revenue {sortBy === 'totalRevenue' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                  </button>
                  <button
                    type="button"
                    className={`dl-sort-btn${sortBy === 'createdAt' ? ' is-active' : ''}`}
                    onClick={() => handleSort('createdAt')}
                  >
                    Created {sortBy === 'createdAt' ? (sortOrder === 'desc' ? '↓' : '↑') : ''}
                  </button>
                </div>
              </div>
            )}

            {links.length === 0 ? (
              <div className="dl-empty">
                <div className="dl-empty-icon"><Link2 size={22} /></div>
                <h3 className="dl-empty-title">No deep links yet</h3>
                <p className="dl-empty-desc">
                  {search
                    ? 'Try a different search.'
                    : 'Create your first tracked link to start collecting analytics.'}
                </p>
                {!search && (
                  <button type="button" className="dl-btn dl-btn-primary" onClick={openCreate}>
                    <Plus size={13} /> Create link
                  </button>
                )}
              </div>
            ) : (
              <ul className="dl-list">
                {links.map(link => (
                  <li
                    key={link.id}
                    className="dl-row"
                    onClick={() => navigate(`/deep-links/${link.id}`)}
                  >
                    <div className="dl-link-row">
                      <span className="dl-link-info">
                        <strong>{link.title || 'Untitled link'}</strong>
                        <em>{link.destinationUrl}</em>
                      </span>
                      <span className="dl-link-short" onClick={e => e.stopPropagation()}>
                        <code>{link.customAlias || link.shortCode}</code>
                        <button
                          type="button"
                          className="dl-copy-btn"
                          onClick={() => handleCopy(link.shortUrl)}
                          aria-label="Copy short link"
                        >
                          <Copy size={11} />
                        </button>
                      </span>
                      <span className="dl-link-stats">
                        <strong>{formatNumber(link.totalClicks)}</strong> clicks ·
                        {' '}{formatNumber(link.uniqueClicks)} unique ·
                        {' '}{link.totalConversions} conv ·
                        {' '}{formatCurrency(link.totalRevenue)}
                      </span>
                      <span className="dl-link-date">
                        {new Date(link.createdAt).toLocaleDateString()}
                      </span>
                      <span className="dl-link-actions" onClick={e => e.stopPropagation()}>
                        {/* On mobile we keep only the QR button visible.
                            Copy lives inside the short-code pill, and
                            Edit/Delete drop because tapping the row goes
                            to the detail page where both are present.
                            All four show on desktop (≥720px). */}
                        <button
                          type="button"
                          className="dl-icon-btn dl-icon-btn-mobile-hide"
                          title="Copy link"
                          aria-label="Copy link"
                          onClick={() => handleCopy(link.shortUrl)}
                        >
                          <Copy size={14} />
                        </button>
                        <button
                          type="button"
                          className="dl-icon-btn"
                          title="QR code"
                          aria-label="Download QR code"
                          onClick={() => handleQR(link)}
                        >
                          <QrCode size={14} />
                        </button>
                        <button
                          type="button"
                          className="dl-icon-btn dl-icon-btn-mobile-hide"
                          title="Edit"
                          aria-label="Edit link"
                          onClick={() => handleEdit(link)}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          type="button"
                          className="dl-icon-btn dl-icon-btn-danger dl-icon-btn-mobile-hide"
                          title="Delete"
                          aria-label="Delete link"
                          onClick={() => handleDelete(link)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {pagination.pages > 1 && (
              <nav className="dl-pagination" aria-label="Pagination">
                <span className="dl-page-info">
                  Showing {((pagination.page - 1) * pagination.limit) + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </span>
                <div className="dl-page-controls">
                  <button
                    type="button"
                    className="dl-page-btn"
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      type="button"
                      className={`dl-page-btn${pagination.page === p ? ' is-active' : ''}`}
                      onClick={() => setPagination(prev => ({ ...prev, page: p }))}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="dl-page-btn"
                    disabled={pagination.page >= pagination.pages}
                    onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                    aria-label="Next page"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              </nav>
            )}
          </section>

          {/* ── Create / Edit Modal ─────────────────────── */}
          <Modal
            isOpen={showCreateModal}
            onClose={closeModal}
            title={editingLink ? 'Edit deep link' : 'Create deep link'}
            size="lg"
            padded={false}
            footer={
              <>
                <button type="button" className="dl-btn dl-btn-secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="dl-link-form"
                  className="dl-btn dl-btn-primary"
                  disabled={saving}
                >
                  {saving ? <Loader2 size={14} className="dl-spin" /> : null}
                  {saving ? 'Saving…' : (editingLink ? 'Update link' : 'Create link')}
                </button>
              </>
            }
          >
            <form
              id="dl-link-form"
              className="dl-form"
              onSubmit={(e) => { e.preventDefault(); handleSave(); }}
            >
              <div className="dl-field">
                <label className="dl-field-label">Destination URL *</label>
                <input
                  className="dl-input"
                  type="url"
                  placeholder="https://yourstore.userevstar.com/products/example"
                  value={formData.destinationUrl}
                  onChange={e => setFormData({ ...formData, destinationUrl: e.target.value })}
                  disabled={!!editingLink}
                  required
                />
                {editingLink && (
                  <span className="dl-field-hint">
                    Destination URL can't be changed after creation — short link tracking stays consistent.
                  </span>
                )}
              </div>

              <div className="dl-form-row">
                <div className="dl-field">
                  <label className="dl-field-label">Title</label>
                  <input
                    className="dl-input"
                    placeholder="e.g. Instagram bio link"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                  />
                </div>
                <div className="dl-field">
                  <label className="dl-field-label">Custom alias</label>
                  <input
                    className="dl-input"
                    placeholder="e.g. summer-sale"
                    value={formData.customAlias}
                    onChange={e => setFormData({ ...formData, customAlias: e.target.value })}
                  />
                  <span className="dl-field-hint">
                    {PLATFORM_DOMAIN}/l/{formData.customAlias || 'abc123'}
                  </span>
                </div>
              </div>

              <div className="dl-form-row">
                <div className="dl-field">
                  <label className="dl-field-label">Campaign</label>
                  <input
                    className="dl-input"
                    placeholder="e.g. Summer 2026"
                    value={formData.campaign}
                    onChange={e => setFormData({ ...formData, campaign: e.target.value })}
                  />
                </div>
                <div className="dl-field">
                  <label className="dl-field-label">Expires on</label>
                  <input
                    className="dl-input"
                    type="date"
                    value={formData.expiresAt}
                    onChange={e => setFormData({ ...formData, expiresAt: e.target.value })}
                  />
                </div>
              </div>

              <div className="dl-utm-block">
                <h3 className="dl-utm-title">
                  <Zap size={13} /> UTM parameters
                </h3>
                <div className="dl-form-row">
                  <div className="dl-field">
                    <label className="dl-field-label">Source</label>
                    <input
                      className="dl-input"
                      placeholder="instagram"
                      value={formData.utmSource}
                      onChange={e => setFormData({ ...formData, utmSource: e.target.value })}
                    />
                  </div>
                  <div className="dl-field">
                    <label className="dl-field-label">Medium</label>
                    <input
                      className="dl-input"
                      placeholder="social"
                      value={formData.utmMedium}
                      onChange={e => setFormData({ ...formData, utmMedium: e.target.value })}
                    />
                  </div>
                </div>
                <div className="dl-form-row">
                  <div className="dl-field">
                    <label className="dl-field-label">Campaign</label>
                    <input
                      className="dl-input"
                      placeholder="summer_sale"
                      value={formData.utmCampaign}
                      onChange={e => setFormData({ ...formData, utmCampaign: e.target.value })}
                    />
                  </div>
                  <div className="dl-field">
                    <label className="dl-field-label">Content</label>
                    <input
                      className="dl-input"
                      placeholder="bio_link"
                      value={formData.utmContent}
                      onChange={e => setFormData({ ...formData, utmContent: e.target.value })}
                    />
                  </div>
                </div>
                {(formData.utmSource || formData.utmMedium || formData.utmCampaign) && (
                  <code className="dl-utm-preview">
                    {formData.destinationUrl || 'https://…'}
                    {formData.utmSource   ? `?utm_source=${formData.utmSource}` : ''}
                    {formData.utmMedium   ? `&utm_medium=${formData.utmMedium}` : ''}
                    {formData.utmCampaign ? `&utm_campaign=${formData.utmCampaign}` : ''}
                    {formData.utmContent  ? `&utm_content=${formData.utmContent}` : ''}
                  </code>
                )}
              </div>
            </form>
          </Modal>

        </div>
      </FeatureGate>
    </Shell>
  );
}
